import React, { useState, useEffect } from 'react';
import { Mail, Search, Filter, Plus, FileText, Download, Trash2, Edit2, Calendar, Link as LinkIcon, Upload, Save, X, Clock, Send, Inbox, AlertTriangle, CheckCircle, XCircle, ChevronDown, MessageSquareText, Briefcase, Sparkles, Loader2, ArrowUpDown } from 'lucide-react';
import ClearFiltersButton from './ClearFiltersButton';

import { OfficialCommunication, CommunicationType, CommunicationStatus, Tender, AnalyzedFile, TenderStatus, CompanyProfile } from '../types';
import { logActivity } from '../services/auditService';
// TODO: Implement extractCommunicationData via API call to backend
// import { extractCommunicationData } from '../services/geminiService';

import { apiService } from '../services/apiService';
import useCompanies from '../hooks/useCompanies';
import { sendEmail } from '../services/emailService';
import { settingsService } from '../services/settingsService';

import { useModal } from './ModalProvider';

const CommunicationManager: React.FC = () => {
  const [communications, setCommunications] = useState<OfficialCommunication[]>([]);
  const [wonTenders, setWonTenders] = useState<Tender[]>([]);
    // companies provided by shared hook
    const { companies, loading: companiesLoading, refresh: refreshCompanies, getCompanyName } = useCompanies();

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<CommunicationType | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<CommunicationStatus | 'ALL'>('ALL');
  const [companyFilter, setCompanyFilter] = useState<string>('ALL'); // Filtro de Empresa
  const [startDate, setStartDate] = useState(''); // Novo Filtro Data Início
  const [endDate, setEndDate] = useState('');     // Novo Filtro Data Fim
  const [sortOrder, setSortOrder] = useState<'DEADLINE_ASC' | 'DEADLINE_DESC' | 'CREATED_DESC'>('DEADLINE_ASC'); // Novo Estado de Ordenação

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingComm, setEditingComm] = useState<OfficialCommunication | null>(null);
  const [formData, setFormData] = useState<Partial<OfficialCommunication>>({
    type: 'OFFICIAL_LETTER_OUT',
    subject: '',
    tenderId: '',
    date: new Date().toISOString().split('T')[0],
    status: 'DRAFT',
    description: '',
    companyId: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Estados da IA
  const [isExtracting, setIsExtracting] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);

    const { showAlert, showConfirm } = useModal();

  useEffect(() => {
        const loadData = async () => {
            try {
                const [commsList, tendersList] = await Promise.all([
                    apiService.getCommunications(),
                    apiService.getTenders()
                ]);

                setCommunications(commsList);
                // Filtrar apenas licitações onde já temos contrato ou estamos em processo (Ganha/Participar/Alta Prioridade)
                setWonTenders(tendersList.filter(t => [TenderStatus.WON, TenderStatus.EXTREME_PRIORITY, TenderStatus.HIGH_PRIORITY].includes(t.status)));
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            }
        };
        loadData();
  }, []);

    const clearFilters = () => {
        setSearchTerm('');
        setTypeFilter('ALL');
        setStatusFilter('ALL');
        setCompanyFilter('ALL');
        setStartDate('');
        setEndDate('');
        setSortOrder('DEADLINE_ASC');
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setAiSuccess(false); // Reseta IA se mudar arquivo manualmente
    }
  };

  // --- Função Mágica: Upload + Extração IA ---
  const handleAiExtraction = async (file: File) => {
      setIsExtracting(true);
      const reader = new FileReader();

      reader.onloadend = async () => {
          const base64String = reader.result as string;
          const base64Data = base64String.split(',')[1];

          // TODO: Implement extractCommunicationData via API call to backend
          // const extracted = await extractCommunicationData(base64Data);
          const extracted = null; // Temporário - implementar via API

          if (extracted) {
              setFormData(prev => ({
                  ...prev,
                  subject: extracted.subject || file.name.replace('.pdf', ''),
                  date: extracted.date || new Date().toISOString().split('T')[0],
                  deadlineResponse: extracted.deadlineResponse || '',
                  type: (extracted.type as CommunicationType) || 'OTHER',
                  description: extracted.description || ''
              }));
              setSelectedFile(file); // Define o arquivo para salvar depois
              setAiSuccess(true);
          } else {
              await showAlert("Não foi possível extrair dados do documento. Tente preencher manualmente.");
          }
          setIsExtracting(false);
      };

      reader.readAsDataURL(file);
  };

  const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          const file = e.dataTransfer.files[0];
          if (file.type === 'application/pdf') {
              handleAiExtraction(file); // Gatilho automático ao soltar
          } else {
              await showAlert("Apenas arquivos PDF são aceitos para leitura inteligente.");
          }
      }
  };

  const handleOpenModal = (comm?: OfficialCommunication) => {
    // Definir empresa padrão (a primeira da lista ou a marcada como default)
    let defaultCompanyId = '';
    if (companies.length > 0) {
        const defaultComp = companies.find(c => c.isDefault);
        defaultCompanyId = defaultComp ? defaultComp.id : companies[0].id;
    }

    if (comm) {
      setEditingComm(comm);
      setFormData(comm);
      setSelectedFile(null);
    } else {
      setEditingComm(null);
      setFormData({
        type: 'OFFICIAL_LETTER_OUT',
        subject: '',
        tenderId: '', // Vazio por padrão (opcional)
        date: new Date().toISOString().split('T')[0],
        status: 'DRAFT',
        description: '',
        companyId: defaultCompanyId
      });
      setSelectedFile(null);
    }
    setAiSuccess(false);
    setIsModalOpen(true);
  };

    const handleSave = async () => {
    // tenderId removido da validação obrigatória
        if (!formData.subject || !formData.date) {
            await showAlert("Preencha o Assunto e a Data.");
            return;
        }

    const processSave = async (fileMeta: AnalyzedFile | null | undefined) => {
        const finalFile = fileMeta !== undefined ? fileMeta : (editingComm ? editingComm.file : null);

        try {
          if (editingComm) {
              const updatedComm = await apiService.updateCommunication(editingComm.id, { ...formData, file: finalFile });
              setCommunications(prev => prev.map(c => c.id === editingComm.id ? updatedComm : c));
              logActivity('OFICIO_ATUALIZACAO', `Documento atualizado: ${formData.subject}`, 'INFO');
          } else {
              const newComm = await apiService.createCommunication({ ...formData, file: finalFile });
              setCommunications(prev => [newComm, ...prev]);
              logActivity('OFICIO_CRIACAO', `Novo documento registrado: ${formData.subject} (${formData.type})`, 'SUCCESS');

              // Tentar enviar notificação por e-mail (silencioso em caso de erro)
              try {
                  const settings = await settingsService.getAppSettings();
                  // Preferir a configuração específica de notificationPreferences.newCommunication (nova estrutura),
                  // mas manter fallback para a configuração legada notifyNewCommunication. Default para true se não configurado.
                  const shouldNotifyNewComm = settings?.notificationPreferences?.newCommunication ?? settings?.notifyNewCommunication ?? true;
                  if (shouldNotifyNewComm && settings?.emailRecipients && settings.emailJsConfig?.serviceId) {
                      const recipients = settings.emailRecipients.split(',').map((r: string) => r.trim());
                      if (recipients.length > 0) {
                          await sendEmail(settings.emailJsConfig, {
                              to_email: recipients[0],
                              subject: `Nova Comunicação: ${formData.subject}`,
                              message: `Uma nova comunicação foi registrada no sistema.\n\nAssunto: ${formData.subject}\nTipo: ${getTypeLabel(formData.type as CommunicationType)}\nData: ${formData.date}\n\nAcesse o sistema para ver os detalhes.`,
                              title: 'Nova Comunicação Registrada',
                              type: 'COMUNICACAO'
                          });
                      }
                  }
              } catch (emailErr) {
                  console.error('Erro silencioso ao enviar e-mail:', emailErr);
              }
          }
          setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao salvar comunicação:', error);
            await showAlert('Erro ao salvar comunicação.');
        }
    };

    // If a file is selected, upload it to S3 first (presigned URL) and send only metadata to the API.
    if (selectedFile) {
        try {
            // Build a relatively unique key for the object
            const safeName = selectedFile.name.replace(/\s+/g, '_');
            const key = `communications/${Date.now()}_${safeName}`;
            const presign = await apiService.getPresignedUploadUrl(key, selectedFile.type || 'application/pdf');

            // Upload to S3 using the presigned PUT URL
            await apiService.uploadFileToS3(presign.uploadUrl, selectedFile);

            // Prepare metadata to send to the backend (small payload)
            const fileMeta: AnalyzedFile = {
                name: selectedFile.name,
                type: selectedFile.type,
                size: selectedFile.size,
                base64: null,
                url: presign.downloadUrl
            };

            await processSave(fileMeta);
        } catch (err) {
            console.error('Erro ao enviar arquivo para S3:', err);
            await showAlert('Falha ao enviar o arquivo. Tente novamente.');
        }
    } else {
        // No file selected — proceed with saving metadata only
        await processSave(undefined);
    }
  };

    const handleDelete = async (id: string, subject: string) => {
        const ok = await showConfirm(`Excluir o documento "${subject}"?`);
        if (!ok) return;
        try {
            await apiService.deleteCommunication(id);
            setCommunications(prev => prev.filter(c => c.id !== id));
            logActivity('OFICIO_EXCLUSAO', `Documento excluído: ${subject}`, 'WARNING');
        } catch (error) {
            console.error('Erro ao excluir comunicação:', error);
            await showAlert('Erro ao excluir comunicação.');
        }
    };

  const handleDownload = async (comm: OfficialCommunication) => {
      if (!comm.file) {
          await showAlert('Nenhum arquivo anexado.');
          return;
      }

      if (comm.file.url) {
          window.open(comm.file.url, '_blank');
          return;
      }

      if (comm.file.base64) {
          const link = document.createElement('a');
          link.href = `data:${comm.file.type};base64,${comm.file.base64}`;
          link.download = comm.file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      } else {
          await showAlert('Arquivo inválido ou corrompido.');
      }
  };

  const getTypeLabel = (type: CommunicationType) => {
      switch (type) {
          case 'NOTIFICATION_IN': return 'Notificação de Cliente';
          case 'EXTENSION_REQUEST': return 'Pedido de Prorrogação';
          case 'OFFICIAL_LETTER_OUT': return 'Ofício Enviado';
          case 'DEFENSE': return 'Defesa Prévia / Recurso';
          case 'OTHER': return 'Outros';
          default: return type;
      }
  };

  const getTypeBadge = (type: CommunicationType) => {
      switch (type) {
          case 'NOTIFICATION_IN': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/60"><AlertTriangle className="w-3 h-3" /> Notificação Recebida</span>;
          case 'EXTENSION_REQUEST': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800/60"><Clock className="w-3 h-3" /> Pedido Prorrogação</span>;
          case 'OFFICIAL_LETTER_OUT': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60"><Send className="w-3 h-3" /> Ofício Enviado</span>;
          case 'DEFENSE': return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200"><MessageSquareText className="w-3 h-3" /> Defesa/Recurso</span>;
          default: return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark">Outros</span>;
      }
  };

  const getStatusBadge = (status: CommunicationStatus) => {
      switch(status) {
          case 'DRAFT': return <span className="text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt px-2 py-1 rounded">Rascunho</span>;
          case 'SENT': return <span className="text-xs font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded flex items-center gap-1"><Send className="w-3 h-3"/> Enviado</span>;
          case 'RECEIVED': return <span className="text-xs font-medium text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded flex items-center gap-1"><Inbox className="w-3 h-3"/> Recebido</span>;
          case 'ACCEPTED': return <span className="text-xs font-medium text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Deferido/Aceito</span>;
          case 'REJECTED': return <span className="text-xs font-medium text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded flex items-center gap-1"><XCircle className="w-3 h-3"/> Indeferido/Rejeitado</span>;
          case 'UNDER_REVIEW': return <span className="text-xs font-medium text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3"/> Em Análise</span>;
      }
  };

  const getTenderInfo = (id: string) => {
      if (!id) return null;
      const tender = wonTenders.find(t => t.id === id);
      return tender ? `${tender.organ} - ${tender.title}` : `Processo ID: ${id} (Não encontrado)`;
  };

    // getCompanyName provided by useCompanies hook

  const filteredComms = communications.filter(c => {
      const matchesSearch = c.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (c.tenderId && getTenderInfo(c.tenderId)?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = typeFilter === 'ALL' || c.type === typeFilter;
      const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
      const matchesCompany = companyFilter === 'ALL' || c.companyId === companyFilter;

      // Filtro de Data
      let matchesDate = true;
      if (startDate) {
          matchesDate = matchesDate && new Date(c.date) >= new Date(startDate);
      }
      if (endDate) {
          matchesDate = matchesDate && new Date(c.date) <= new Date(endDate);
      }

      return matchesSearch && matchesType && matchesStatus && matchesDate && matchesCompany;
  });

  // Lógica de Ordenação
  const sortedComms = [...filteredComms].sort((a, b) => {
      if (sortOrder === 'DEADLINE_ASC') {
          // Itens com prazo vêm primeiro, do mais próximo para o mais distante
          if (a.deadlineResponse && !b.deadlineResponse) return -1;
          if (!a.deadlineResponse && b.deadlineResponse) return 1;
          if (!a.deadlineResponse && !b.deadlineResponse) return 0;
          return new Date(a.deadlineResponse!).getTime() - new Date(b.deadlineResponse!).getTime();
      } else if (sortOrder === 'DEADLINE_DESC') {
          // Itens com prazo vêm primeiro, do mais distante para o mais próximo
          if (a.deadlineResponse && !b.deadlineResponse) return -1;
          if (!a.deadlineResponse && b.deadlineResponse) return 1;
          if (!a.deadlineResponse && !b.deadlineResponse) return 0;
          return new Date(b.deadlineResponse!).getTime() - new Date(a.deadlineResponse!).getTime();
      } else {
          // Default: Data de criação ou data do documento (Mais recente primeiro)
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA;
      }
  });

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Gestão de Ofícios & Prazos
            </h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
              Centralize notificações recebidas, pedidos de prorrogação e ofícios de processos em andamento.
            </p>
          </div>
                    <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleOpenModal()}
                                        className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors shadow-sm"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Novo Documento
                                    </button>
                                    <ClearFiltersButton onClear={clearFilters} />
                    </div>
        </div>

        {/* Filtros em Grid */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* Search */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                    type="text"
                    placeholder="Buscar por assunto ou processo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
            </div>

            {/* Intervalo de Data */}
            <div className="relative flex items-center gap-2 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-lg px-2">
                <Calendar className="w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark flex-shrink-0" />
                <div className="flex-1 flex gap-1 items-center">
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full text-xs text-ink-subtitle dark:text-ink-subtitle-dark bg-transparent outline-none py-2.5"
                        title="Data Inicial"
                    />
                    <span className="text-ink-placeholder dark:text-ink-placeholder-dark">-</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full text-xs text-ink-subtitle dark:text-ink-subtitle-dark bg-transparent outline-none py-2.5"
                        title="Data Final"
                    />
                </div>
            </div>

            {/* Ordenação */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ArrowUpDown className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as any)}
                    className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer"
                >
                    <option value="DEADLINE_ASC">Prazo Resposta: Mais Próximo</option>
                    <option value="DEADLINE_DESC">Prazo Resposta: Mais Distante</option>
                    <option value="CREATED_DESC">Mais Recentes (Criação)</option>
                </select>
            </div>

            {/* Filtro de Empresa (NOVO) */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none cursor-pointer"
                >
                    <option value="ALL">Empresa: Todas</option>
                    {companies.map(comp => (
                        <option key={comp.id} value={comp.id}>{comp.tradeName}</option>
                    ))}
                </select>
            </div>
        </div>
      </div>

      {/* Listagem */}
      <div className="grid grid-cols-1 gap-4">
          {sortedComms.length > 0 ? (
              sortedComms.map(comm => (
                  <div key={comm.id} className="bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark p-5 shadow-sm hover:shadow-md transition-all">
                      <div className="flex flex-col md:flex-row justify-between items-start gap-4">

                          {/* Conteúdo Esquerda */}
                          <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                  {getTypeBadge(comm.type)}
                                  <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark flex items-center gap-1">
                                      <Calendar className="w-3 h-3" /> {new Date(comm.date).toLocaleDateString('pt-BR')}
                                  </span>
                                  {comm.deadlineResponse && (
                                      <span className="text-xs font-bold text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded border border-red-100 dark:border-red-800/50 flex items-center gap-1">
                                          <Clock className="w-3 h-3" /> Responder até: {new Date(comm.deadlineResponse).toLocaleDateString('pt-BR')}
                                      </span>
                                  )}
                              </div>

                              <h3 className="text-base font-bold text-ink-title dark:text-ink-title-dark mb-1">{comm.subject}</h3>

                              <div className="flex flex-wrap items-center gap-2 mb-3">
                                  <div className={`flex items-center gap-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark bg-blue-50 dark:bg-blue-900/30/50 p-1.5 rounded-lg border border-blue-100 w-fit ${!comm.tenderId ? 'opacity-50 grayscale' : ''}`}>
                                      <LinkIcon className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                      {comm.tenderId ? (
                                          <>
                                            <span className="font-medium text-blue-800 dark:text-blue-200">Processo:</span> {getTenderInfo(comm.tenderId)}
                                          </>
                                      ) : (
                                          <span className="italic text-ink-subtitle dark:text-ink-subtitle-dark">Sem vínculo com processo</span>
                                      )}
                                  </div>

                                  {comm.companyId && (
                                      <div className="flex items-center gap-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark bg-surface-alt dark:bg-surface-dark-alt p-1.5 rounded-lg border border-line dark:border-line-dark w-fit">
                                          <Briefcase className="w-3.5 h-3.5 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                          <span className="font-medium">{getCompanyName(comm.companyId)}</span>
                                      </div>
                                  )}
                              </div>

                              {comm.description && (
                                  <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark line-clamp-2 italic border-l-2 border-line dark:border-line-dark pl-3">
                                      "{comm.description}"
                                  </p>
                              )}
                          </div>

                          {/* Ações Direita */}
                          <div className="flex flex-col items-end gap-3 min-w-[140px]">
                              {getStatusBadge(comm.status)}

                              <div className="flex gap-2 mt-2">
                                  {comm.file && (
                                      <button
                                          onClick={() => handleDownload(comm)}
                                          className="p-2 text-blue-600 dark:text-blue-300 hover:bg-blue-50 rounded-lg border border-transparent hover:border-blue-100 transition-colors"
                                          title={`Baixar ${comm.file.name}`}
                                      >
                                          <Download className="w-4 h-4" />
                                      </button>
                                  )}
                                  <button
                                      onClick={() => handleOpenModal(comm)}
                                      className="p-2 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-blue-600 hover:bg-surface-alt dark:bg-surface-dark-alt rounded-lg transition-colors"
                                      title="Editar"
                                  >
                                      <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                      onClick={() => handleDelete(comm.id, comm.subject)}
                                      className="p-2 text-ink-subtitle dark:text-ink-subtitle-dark hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Excluir"
                                  >
                                      <Trash2 className="w-4 h-4" />
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>
              ))
          ) : (
              <div className="py-16 text-center text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark border-dashed">
                  <div className="flex justify-center mb-3">
                      <FileText className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  </div>
                  <p>Nenhuma comunicação encontrada.</p>
                  <p className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark mt-1">Utilize o botão "Novo Documento" para adicionar notificações, pedidos ou ofícios.</p>
              </div>
          )}
      </div>

      {/* Modal Criar/Editar */}
      {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                  <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
                      <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                          <Mail className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          {editingComm ? 'Editar Documento' : 'Novo Ofício / Notificação'}
                      </h3>
                      <button onClick={() => setIsModalOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">
                          <X className="w-5 h-5" />
                      </button>
                  </div>

                  <div className="p-6 overflow-y-auto space-y-6">

                      {/* Área de Leitura Inteligente (IA) */}
                      {!editingComm && (
                          <div
                              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer relative overflow-hidden group ${
                                  aiSuccess ? 'border-green-300 bg-green-50 dark:bg-green-900/30' : 'border-purple-300 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100'
                              }`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={onDropFile}
                          >
                              {isExtracting ? (
                                  <div className="flex flex-col items-center justify-center py-4">
                                      <Loader2 className="w-8 h-8 text-purple-600 dark:text-purple-300 animate-spin mb-2" />
                                      <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Analisando documento com IA...</p>
                                      <p className="text-xs text-purple-600 dark:text-purple-300">Extraindo datas, prazos e assunto</p>
                                  </div>
                              ) : aiSuccess ? (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300 mb-2" />
                                      <p className="text-sm font-bold text-green-800 dark:text-green-200">Dados Extraídos!</p>
                                      <p className="text-xs text-green-600 dark:text-green-300">Verifique e edite os campos abaixo se necessário.</p>
                                  </div>
                              ) : (
                                  <div className="flex flex-col items-center justify-center py-2">
                                      <div className="bg-surface dark:bg-surface-dark p-3 rounded-full shadow-sm mb-3">
                                          <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-300" />
                                      </div>
                                      <h4 className="font-bold text-purple-900 mb-1">Preenchimento Automático via IA</h4>
                                      <p className="text-xs text-purple-700 max-w-xs mx-auto mb-3">
                                          Arraste o PDF do Ofício/Notificação aqui para que a IA identifique o assunto, datas e prazos de resposta.
                                      </p>
                                      <label className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer shadow-sm">
                                          Selecionar PDF
                                          <input
                                              type="file"
                                              accept=".pdf"
                                              className="hidden"
                                              onChange={(e) => {
                                                  if (e.target.files && e.target.files[0]) {
                                                      handleAiExtraction(e.target.files[0]);
                                                  }
                                              }}
                                          />
                                      </label>
                                  </div>
                              )}
                          </div>
                      )}

                      {/* Vínculo com Processo (Essencial) */}
                      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-100">
                          <label className="block text-xs font-bold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                              <LinkIcon className="w-3 h-3" />
                              Vincular ao Processo (Licitação Ganha) <span className="font-normal text-blue-600 dark:text-blue-300 ml-1">(Opcional)</span>
                          </label>
                          <div className="relative">
                              <select
                                  className="w-full border border-blue-200 dark:border-blue-800/60 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark appearance-none pr-8"
                                  value={formData.tenderId}
                                  onChange={e => setFormData({...formData, tenderId: e.target.value})}
                              >
                                  <option value="">Selecione um processo...</option>
                                  {wonTenders.map(t => (
                                      <option key={t.id} value={t.id}>
                                          {t.organ} - {t.title.substring(0, 50)}...
                                      </option>
                                  ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark pointer-events-none" />
                          </div>
                      </div>

                      {/* Vínculo com Empresa */}
                      <div>
                          <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1 flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              Empresa Responsável
                          </label>
                          <select
                              className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark text-ink-text dark:text-ink-text-dark"
                              value={formData.companyId}
                              onChange={e => setFormData({...formData, companyId: e.target.value})}
                          >
                              <option value="">Selecione a empresa...</option>
                              {companies.map(c => (
                                  <option key={c.id} value={c.id}>{c.tradeName} ({c.cnpj})</option>
                              ))}
                          </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Tipo de Documento *</label>
                              <select
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                                  value={formData.type}
                                  onChange={e => setFormData({...formData, type: e.target.value as CommunicationType})}
                              >
                                  <option value="NOTIFICATION_IN">Notificação de Cliente (Recebida)</option>
                                  <option value="EXTENSION_REQUEST">Pedido de Prorrogação de Prazo</option>
                                  <option value="OFFICIAL_LETTER_OUT">Ofício Geral (Enviado)</option>
                                  <option value="DEFENSE">Defesa Prévia / Recurso</option>
                                  <option value="OTHER">Outros</option>
                              </select>
                          </div>

                          <div className="col-span-2">
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Assunto / Título *</label>
                              <input
                                  type="text"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  placeholder="Ex: Pedido de Prorrogação - Atraso Fabricante"
                                  value={formData.subject}
                                  onChange={e => setFormData({...formData, subject: e.target.value})}
                              />
                          </div>

                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Data do Documento *</label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={formData.date}
                                  onChange={e => setFormData({...formData, date: e.target.value})}
                              />
                          </div>

                          <div>
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1 flex items-center gap-1">
                                  Prazo para Resposta
                                  <span className="text-ink-placeholder dark:text-ink-placeholder-dark font-normal">(Opcional)</span>
                              </label>
                              <input
                                  type="date"
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                                  value={formData.deadlineResponse || ''}
                                  onChange={e => setFormData({...formData, deadlineResponse: e.target.value})}
                              />
                          </div>

                          <div className="col-span-2">
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Status Atual</label>
                              <div className="flex gap-2 overflow-x-auto pb-1">
                                  {(['DRAFT', 'SENT', 'RECEIVED', 'ACCEPTED', 'REJECTED', 'UNDER_REVIEW'] as CommunicationStatus[]).map(status => (
                                      <button
                                          key={status}
                                          onClick={() => setFormData({...formData, status})}
                                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                                              formData.status === status
                                              ? 'bg-brand-500 text-white border-blue-600 dark:border-blue-500'
                                              : 'bg-surface dark:bg-surface-dark text-ink-subtitle dark:text-ink-subtitle-dark border-line dark:border-line-dark hover:bg-surface-alt dark:bg-surface-dark-alt'
                                          }`}
                                      >
                                          {status === 'DRAFT' ? 'Rascunho' :
                                           status === 'SENT' ? 'Enviado' :
                                           status === 'RECEIVED' ? 'Recebido' :
                                           status === 'ACCEPTED' ? 'Aceito' :
                                           status === 'REJECTED' ? 'Rejeitado' : 'Em Análise'}
                                      </button>
                                  ))}
                              </div>
                          </div>

                          <div className="col-span-2">
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Descrição / Resumo</label>
                              <textarea
                                  className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none h-20"
                                  placeholder="Detalhes adicionais sobre a solicitação ou notificação..."
                                  value={formData.description}
                                  onChange={e => setFormData({...formData, description: e.target.value})}
                              />
                          </div>

                          {/* Upload */}
                          <div className="col-span-2">
                              <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                                  {selectedFile ? 'Arquivo Selecionado' : 'Anexar Arquivo (PDF)'}
                              </label>
                              <div className="flex items-center gap-3">
                                  <label className="flex-1 cursor-pointer bg-surface-alt dark:bg-surface-dark-alt hover:bg-surface-alt dark:bg-surface-dark-alt border border-line-strong dark:border-line-dark-strong border-dashed rounded-lg p-3 text-center transition-colors group">
                                      <div className="flex flex-col items-center justify-center gap-1">
                                          <Upload className="w-5 h-5 text-ink-placeholder dark:text-ink-placeholder-dark group-hover:text-blue-500" />
                                          <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Clique para selecionar PDF</span>
                                      </div>
                                      <input
                                          type="file"
                                          accept=".pdf"
                                          className="hidden"
                                          onChange={handleFileUpload}
                                      />
                                  </label>
                                  {editingComm?.file && !selectedFile && (
                                      <div className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-2 rounded-lg border border-green-100 flex items-center gap-2">
                                          <FileText className="w-4 h-4" />
                                          <span className="truncate max-w-[150px]">{editingComm.file.name}</span>
                                      </div>
                                  )}
                                  {selectedFile && (
                                      <div className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg border border-blue-100 flex items-center gap-2">
                                          <FileText className="w-4 h-4" />
                                          <span className="truncate max-w-[150px]">{selectedFile.name}</span>
                                      </div>
                                  )}
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
                      <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt">Cancelar</button>
                      <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm">
                          <Save className="w-4 h-4" /> Salvar Documento
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default CommunicationManager;
